import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext.js';
import { toast } from 'react-toastify';
import newsletterApi from '../../services/newsletterApi.js';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext.js';
import { useSiteBrand } from '../../contexts/SiteBrandContext.js';
import { ShopFloatWidgets, BrandSocialIcon, brandSocialLinks } from './ShopSocialBar.js';

const Icon = ({ path, className = "h-5 w-5" }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} /></svg>;

const FLARE_RED = '#c4151c';
const FLARE_RED_DARK = '#9f1016';

function ShopNavbar() {
  const navigate = useNavigate();
  const brand = useSiteBrand();
  const { totalUniqueItems, subtotal } = useCart();
  const { user, logout, isAuthenticated } = useCustomerAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const acctRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (acctRef.current && !acctRef.current.contains(e.target)) setAcctOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleLogout = async () => {
    setAcctOpen(false);
    try { await logout(); toast.success('Logged out successfully'); } catch (e) { toast.error(e?.message || 'Logout failed'); }
    navigate('/', { replace: true });
  };

  const navItems = [
    { to: '/', label: 'Home', end: true },
    { to: '/magazines', label: 'Magazines' },
    { to: '/plans', label: 'Subscriptions' },
    { to: '/#features', label: 'Features' },
    { to: '/how', label: 'How it Works' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-[0_1px_8px_-2px_rgba(0,0,0,0.08)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="group">
              <div
                className="flex flex-col items-center justify-center rounded-xl px-4 py-2 bg-white shadow-[0_2px_14px_-3px_rgba(196,21,28,0.3)] border border-gray-100 group-hover:shadow-[0_6px_20px_-4px_rgba(196,21,28,0.4)] transition-shadow"
              >
                <img
                  key={brand.logo}
                  src={brand.loading ? undefined : brand.logo}
                  alt="Flare Magazine"
                  className="h-10 w-auto max-w-[150px] object-contain drop-shadow-sm"
                  style={{ visibility: brand.loading ? 'hidden' : 'visible' }}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '/Flare Logo.png';
                  }}
                />
                <div className="w-full text-[10px] text-gray-500 uppercase tracking-[0.22em] font-bold leading-none mt-0 text-center">
                  {brand.domain}
                </div>
              </div>
            </Link>
          </div>

          <nav className="hidden lg:flex items-center gap-0.5">
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `px-4 py-2.5 rounded-lg text-sm font-bold transition-all ${
                  isActive
                    ? 'text-red-700 bg-rose-50'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="relative hidden sm:block" ref={acctRef}>
              <button
                onClick={() => setAcctOpen(v => !v)}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg font-bold text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors border border-gray-200"
              >
                <Icon path="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" className="h-4 w-4" />
                {isAuthenticated && user ? (
                  <span className="flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black text-white" style={{ backgroundColor: FLARE_RED }}>
                      {(user.name || 'U').trim().slice(0, 1).toUpperCase()}
                    </span>
                    My Account
                  </span>
                ) : 'Login / Register'}
                <svg className={`h-3.5 w-3.5 transition-transform ${acctOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {acctOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 rounded-xl shadow-xl border border-gray-100 bg-white overflow-hidden z-50">
                  {isAuthenticated && user ? (
                    <>
                      <div className="p-4 border-b border-gray-100" style={{ background: `linear-gradient(135deg, #fff1f2 0%, #ffffff 100%)` }}>
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-full flex items-center justify-center font-black text-white shadow-sm" style={{ backgroundColor: FLARE_RED }}>
                            {(user.name || 'U').trim().slice(0, 1).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="font-black text-gray-900 truncate">{user.name}</div>
                            <div className="text-xs text-gray-500 truncate font-semibold">{user.email}</div>
                          </div>
                        </div>
                      </div>
                      <Link to="/account/dashboard" onClick={() => setAcctOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-rose-50 hover:text-red-700">
                        <Icon path="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" className="h-4 w-4" />
                        Dashboard
                      </Link>
                      <Link to="/account/subscriptions" onClick={() => setAcctOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-rose-50 hover:text-red-700">
                        <Icon path="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" className="h-4 w-4" />
                        Subscriptions
                      </Link>
                      <Link to="/account/profile" onClick={() => setAcctOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-rose-50 hover:text-red-700">
                        <Icon path="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" className="h-4 w-4" />
                        Profile
                      </Link>
                      <div className="border-t border-gray-100 my-1"></div>
                      <button onClick={handleLogout} className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50">
                        <Icon path="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" className="h-4 w-4" />
                        Log out
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="p-4 border-b border-gray-100" style={{ background: `linear-gradient(135deg, #fff1f2 0%, #ffffff 100%)` }}>
                        <div className="font-black text-gray-900">Welcome to Flare</div>
                        <div className="text-xs text-gray-500 font-semibold mt-0.5">Sign in to track deliveries</div>
                      </div>
                      <Link to="/login" onClick={() => setAcctOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-rose-50 hover:text-red-700">
                        <Icon path="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" className="h-4 w-4" />
                        Login
                      </Link>
                      <Link to="/register" onClick={() => setAcctOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm font-bold" style={{ color: FLARE_RED }}>
                        <Icon path="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" className="h-4 w-4" />
                        Create Account
                      </Link>
                      <div className="border-t border-gray-100 p-4">
                        <Link to="/plans" onClick={() => setAcctOpen(false)} className="block text-center text-xs font-bold uppercase tracking-widest text-gray-500 hover:text-red-700">
                          View Subscription Plans →
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <Link
              to="/cart"
              className="relative inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-bold text-sm transition-all shadow-sm hover:shadow-md"
              style={{
                color: '#ffffff',
                backgroundColor: FLARE_RED,
              }}
            >
              <Icon path="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" className="h-4.5 w-4.5" />
              <span className="hidden sm:inline">Cart</span>
              {totalUniqueItems > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[24px] h-[24px] px-1.5 rounded-full text-[11px] font-black flex items-center justify-center shadow-md ring-2 ring-white" style={{ backgroundColor: '#ffffff', color: FLARE_RED }}>
                  {totalUniqueItems > 99 ? '99+' : totalUniqueItems}
                </span>
              )}
              {subtotal > 0 && (
                <span className="hidden md:inline text-xs font-black opacity-90">
                  Rs {subtotal.toLocaleString('en-PK')}
                </span>
              )}
            </Link>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
              aria-label="Toggle menu"
            >
              <Icon path={mobileOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} className="h-5 w-5" />
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden pb-4 border-t border-gray-100 pt-3 space-y-1">
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) => `block px-4 py-2.5 rounded-lg text-sm font-bold transition-colors ${
                  isActive
                    ? 'text-red-700 bg-rose-50'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {item.label}
              </NavLink>
            ))}
            <div className="mt-2 pt-2 border-t border-gray-100 px-2">
              <div className="text-[11px] font-black uppercase tracking-[0.2em] text-gray-400 px-2 mb-1">My Account</div>
              {isAuthenticated && user ? (
                <>
                  <Link to="/account/dashboard" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold text-gray-700 hover:bg-rose-50">
                    <Icon path="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" className="h-4 w-4" />
                    Dashboard
                  </Link>
                  <Link to="/account/subscriptions" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold text-gray-700 hover:bg-rose-50">
                    <Icon path="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" className="h-4 w-4" />
                    My Subscriptions
                  </Link>
                  <Link to="/account/profile" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold text-gray-700 hover:bg-rose-50">
                    <Icon path="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" className="h-4 w-4" />
                    Profile Settings
                  </Link>
                  <button onClick={() => { setMobileOpen(false); handleLogout(); }} className="w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold text-red-700 hover:bg-red-50">
                    <Icon path="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" className="h-4 w-4" />
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold text-gray-700 hover:bg-rose-50">
                    <Icon path="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" className="h-4 w-4" />
                    Login
                  </Link>
                  <Link to="/register" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-bold" style={{ color: FLARE_RED }}>
                    <Icon path="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" className="h-4 w-4" />
                    Create Account
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

function NewsletterForm({ source = 'Footer' }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.email && !form.phone) { toast.warn('Please enter either your email or phone number.'); return; }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { toast.warn('Please enter a valid email address.'); return; }
    if (form.phone && form.phone.replace(/\D/g, '').length < 10) { toast.warn('Please enter a valid phone number (at least 10 digits).'); return; }
    setLoading(true);
    try {
      const r = await newsletterApi.subscribe({ ...form, source });
      if (r.duplicate) toast.warn(`Already subscribed! Welcome back, ${r.record.name || 'friend'}.`);
      else toast.success(`✅ Subscribed! Welcome aboard, ${r.record.name || 'friend'}!`);
      setForm({ name: '', email: '', phone: '' });
    } catch (e) { toast.error(e.message || 'Failed to subscribe, please try again.'); }
    finally { setLoading(false); }
  };
  return (
    <form className="flex flex-wrap gap-2 w-full min-w-0" onSubmit={submit}>
      <input
        type="text"
        value={form.name}
        onChange={set('name')}
        placeholder="Your name"
        className="flex-1 min-w-[9.5rem] px-4 py-3.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-300"
      />
      <input
        type="email"
        value={form.email}
        onChange={set('email')}
        placeholder="your@email.com"
        className="flex-1 min-w-[10.5rem] px-4 py-3.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-300"
      />
      <input
        type="tel"
        value={form.phone}
        onChange={set('phone')}
        placeholder="+92 300 1234567"
        className="flex-1 min-w-[10.5rem] px-4 py-3.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-300"
      />
      <button
        type="submit"
        disabled={loading}
        className="shrink-0 whitespace-nowrap px-6 py-3.5 rounded-xl font-black text-sm text-white shadow-md hover:shadow-lg transition-shadow disabled:opacity-60"
        style={{ backgroundColor: FLARE_RED }}
      >
        {loading ? 'Subscribing…' : 'Subscribe'}
      </button>
    </form>
  );
}

function ShopFooter() {
  const brand = useSiteBrand();
  const socials = brandSocialLinks(brand);

  return (
    <footer id="contact" className="mt-20 border-t border-gray-200 bg-white">
      <div className="bg-gradient-to-br from-rose-50 via-white to-indigo-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <div className="flex flex-col sm:flex-row items-center sm:items-end gap-5 sm:gap-7">
            <img
              src="/stay-updated.png?v=1"
              alt=""
              className="w-36 h-36 sm:w-44 sm:h-44 -mt-14 sm:-mt-16 rounded-[1.75rem] object-cover shrink-0 shadow-2xl ring-4 ring-white bg-[#fbf7f2]"
            />
            <div className="flex-1 min-w-0 w-full pb-1">
              <div className="text-xs font-bold uppercase tracking-[0.2em] mb-1.5" style={{ color: FLARE_RED }}>
                Stay Updated
              </div>
              <h3 className="text-2xl sm:text-3xl font-black text-gray-900 mb-1.5">
                Never miss a new issue
              </h3>
              <p className="text-gray-600 text-sm max-w-xl mb-4">
                Subscribe to our newsletter and get early access to new releases, exclusive interviews, and special discounts.
              </p>
              <NewsletterForm source="Footer" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="mb-4 inline-block">
              <div className="flex flex-col items-center justify-center rounded-xl px-4 py-2 bg-white shadow-md border border-gray-100">
                <img
                  key={brand.footerLogo}
                  src={brand.loading ? undefined : brand.footerLogo}
                  alt="Flare Magazine"
                  className="h-10 w-auto max-w-[150px] object-contain drop-shadow-sm"
                  style={{ visibility: brand.loading ? 'hidden' : 'visible' }}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '/Flare Logo.png';
                  }}
                />
                <div className="w-full text-[10px] text-gray-500 uppercase tracking-[0.22em] font-bold leading-none mt-0 text-center">
                  {brand.domain}
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed max-w-md mb-4">
              Pakistan's leading monthly magazine — lifestyle, entertainment, culture, politics, and exclusive interviews with the biggest names. Delivered to your doorstep or inbox, your choice.
            </p>
            <div className="flex items-center gap-2.5 flex-wrap">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  title={s.label}
                  aria-label={s.label}
                  className="transition-transform hover:-translate-y-0.5 hover:scale-105"
                >
                  <BrandSocialIcon network={s} size={36} />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
              <div>
                <h4 className="font-black text-gray-900 mb-4 uppercase tracking-wider text-xs">Quick Links</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li><Link to="/plans" className="hover:text-red-700 font-semibold">Subscription Plans</Link></li>
                  <li><Link to="/magazines" className="hover:text-red-700 font-semibold">All Magazine Issues</Link></li>
                  <li><Link to="/cart" className="hover:text-red-700 font-semibold">Shopping Cart</Link></li>
                  <li><Link to="/checkout" className="hover:text-red-700 font-semibold">Checkout</Link></li>
                  <li><a href="/#features" className="hover:text-red-700 font-semibold">Why Flare</a></li>
                </ul>
              </div>

              <div>
                <h4 className="font-black text-gray-900 mb-4 uppercase tracking-wider text-xs">Contact & Support</h4>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center flex-shrink-0" style={{ color: FLARE_RED }}>
                      <Icon path="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" className="h-4 w-4" />
                    </div>
                    <a href={brand.mailHref} className="font-semibold hover:text-red-700 leading-none">{brand.email}</a>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center flex-shrink-0" style={{ color: FLARE_RED }}>
                      <Icon path="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" className="h-4 w-4" />
                    </div>
                    <a href={brand.telHref} className="font-semibold hover:text-red-700 leading-none">{brand.phone}</a>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center flex-shrink-0" style={{ color: FLARE_RED }}>
                      <Icon path="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" className="h-4 w-4" />
                    </div>
                    <span className="font-semibold leading-none">Lahore, Pakistan</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-100">
              <p className="font-black text-gray-900 mb-2.5 uppercase tracking-wider text-xs">Payment Methods</p>
              <div className="flex items-center gap-2 flex-nowrap overflow-x-auto overflow-y-hidden leading-none" aria-label="Accepted payment methods">
                {[
                  { src: '/payments/jazzcash.svg', alt: 'JazzCash' },
                  { src: '/payments/easypaisa.svg', alt: 'EasyPaisa' },
                  { src: '/payments/bank.svg', alt: 'Bank Transfer' },
                  { src: '/payments/visa.svg?v=3', alt: 'Visa' },
                  { src: '/payments/mastercard.svg', alt: 'Mastercard' },
                ].map((p) => (
                  <img
                    key={p.alt}
                    src={p.src}
                    alt={p.alt}
                    title={p.alt}
                    className="h-8 w-auto shrink-0 rounded-md shadow-sm ring-1 ring-black/10 bg-white block"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div id="about" className="mt-6 pt-5 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-500 font-semibold">
            © {new Date().getFullYear()} Flare Magazine • Digital Flare Network • All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs text-gray-500 font-semibold">
            <Link to="/privacy-policy" className="hover:text-red-700">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-red-700">Terms of Service</Link>
            <Link to="/refund-policy" className="hover:text-red-700">Refund Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function ShopLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-white font-sans text-gray-900 antialiased">
      <ShopNavbar />
      <main className="flex-1">
        {children}
      </main>
      <ShopFooter />
      <ShopFloatWidgets />
    </div>
  );
}
